/* biome-ignore-all lint/performance/noReExportAll: grandfathered barrel file */
import logger from '@/utils/logger'
import type { NextRouter } from '@/utils/types'
import { CANONICAL_PARAMS } from '@/constants/params'
import { ShwEnv } from '@/constants/types'

import { SAT_PREP_COURSE, type SATUserType } from '@/queries/constants'
import type { UserContextType } from '@/client/context/user'
import type { ClassroomsPage } from '@/client/services/classroom'
import { getExperimentArm } from '@/client/services/experiments/session-simulator'
import {
  PROGRAM_WORLD_TO_PROGRAM_SLUG,
  type ProgramSlug,
} from '@/client/services/programs/constants'
import encodeInvitationParams from '@/client/services/satBootcamp/encodeInvitationParams'
import type { SATBootcampURLParams } from '@/client/services/satBootcamp/types'
import { DROPIN_QUERY_PARAM } from '@/client/services/session/constants'
import {
  ADJUST_AS_NEEDED_TRAINING_SLUG,
  DEVELOP_CONNECTIONS_TRAINING_SLUG,
  ENGAGE_ACTIVELY_TRAINING_SLUG,
  LAY_THE_FOUNDATION_TRAINING_SLUG,
} from '@/client/services/training'
import { addReferralCodeIfNotPresent } from '@/client/services/url/helpers'
import type { PRIVACY_TAGS } from '@/client/services/url/trustSafety'
import { isProgramWorld } from '@/client/services/worlds/helpers'
import { IS_DEVELOPMENT, WEBSITE_SETTINGS, WEBSITE_SETTINGS_MAP } from '@/client/settings'

import type { AssessmentAssignment } from '@/components/Assessments/types'

import type { BlogArticleCategory } from '../blog'
// importing directly from ./constants to avoid circular dependency
import { WEBSITE_URL } from './constants'
import * as faq from './faq'
import {
  DUPLICATE_OF,
  type QueryParams,
  queryParamsToString,
  SERIES,
  SESSION,
  SESSION_FEEDBACK,
  USER_ID,
} from './queryParams'

export const QUERY_POST_ID = 'p'
export const WORLD_SLUG_QUERY_PARAM_KEY = 'w'
export const SERIES_REFERRAL_USER_QUERY_PARAM = 'sref'

const INVALID_URL_MESSAGE = 'Invalid input url, should not contain a leading slash'
export const ASSETS_URL = `${WEBSITE_URL}/assets`
export const AVATARS_URL = `https://avatars.${WEBSITE_SETTINGS_MAP[ShwEnv.PRODUCTION].websiteName}`
export const COMMUNITY_SLACK_URL = 'https://schoolhouse-community.slack.com'

export const SAT_BOOTCAMP_REFERRAL_CODE_QUERY_PARAM = 'ref_sat'
export const SAT_BOOTCAMP_INVITE_CODE_QUERY_PARAM = 'invite_code'
export const SAT_BOOTCAMP_AS_QUERY_PARM = 'as'
export const SAT_BOOTCAMP_ENCODED_PARAMS_QUERY_KEY = 'params'

export function getCurrentCanonical(router: NextRouter): string {
  const urlSearchParams = new URLSearchParams(Object.entries(router.query) as string[][])
  const pathname = router.pathname.replace(/\[\w+\]/g, key => {
    const actualKey = key.substring(1, key.length - 1)
    const value = urlSearchParams.get(actualKey) ?? ''
    urlSearchParams.delete(actualKey)
    return value
  })

  for (const key of Array.from(urlSearchParams.keys())) {
    if (!CANONICAL_PARAMS.has(key)) {
      urlSearchParams.delete(key)
    }
  }

  urlSearchParams.sort()
  const paramString = `?${urlSearchParams.toString()}`

  return `${WEBSITE_URL}${pathname}${paramString.length > 1 ? paramString : ''}`
}

const SHOW_DASHBOARD_SESSIONS_URL_QUERY = {
  key: 'show',
  options: {
    past: 'past',
    upcoming: 'upcoming',
  },
}

export function getUrl(
  subpath:
    | 'dashboard'
    | 'notifications'
    | 'messages'
    | 'schedule'
    | 'explore'
    | 'tutor'
    | 'admin'
    | 'discover'
): string {
  return `${WEBSITE_URL}/${subpath}`
}

export function getTutorUrl(
  subpath?:
    | 'training'
    | 'assessments'
    | 'feedback'
    | 'peer-review'
    | 'volunteer-hours'
    | 'one-on-one-signup'
    | 'scheduled-help'
): string {
  return `${getUrl('tutor')}${subpath == null ? '' : `/${subpath}`}`
}

export function getWorldScheduleUrl(
  slug: string,
  {
    path,
    queryParams,
  }: {
    path: 'session' | 'series'
    queryParams?: QueryParams
  }
): string {
  const base = `${getWorldUrl(slug)}/schedule`

  return `${base}/${path}${queryParamsToString(queryParams)}`
}

export function getTutorPeerReviewUrl({
  path,
  worldSlug,
}: {
  path?: 'past' | 'leaderboard'
  worldSlug?: string
} = {}): string {
  const base = getTutorUrl('peer-review')

  const urlParams = new URLSearchParams()
  if (worldSlug != null) {
    urlParams.set(WORLD_SLUG_QUERY_PARAM_KEY, worldSlug)
  }

  return `${base}/${path ?? ''}${urlParams.size === 0 ? '' : `?${urlParams.toString()}`}`
}

export function getConversationUrl(externalUuid: string): string {
  return `${getUrl('messages')}#${externalUuid}`
}

export function getTutorTrainingUrl({
  completed = false,
  slug,
}: {
  completed?: boolean
  slug?: string | null
}): string {
  return `${getTutorUrl('training')}?show=${completed ? 'completed' : 'incomplete'}${
    slug != null ? `&slug=${slug}` : ''
  }`
}

export function getTutorOfferingPageUrl({
  type,
  slug,
  subpage,
}: {
  type: 'subject' | 'program'
  slug: string
  subpage?: 'certify'
}): string {
  return `${getUrl('tutor')}?${type}=${slug}${subpage == null ? '' : `&subpage=${subpage}`}`
}

export function getTutorOfferingPageUrlByWorld({
  worldSlug,
  subpage,
}: {
  worldSlug: string
  subpage?: 'certify'
}): string {
  return getTutorOfferingPageUrl({
    type: isProgramWorld(worldSlug) ? 'program' : 'subject',
    slug: isProgramWorld(worldSlug) ? PROGRAM_WORLD_TO_PROGRAM_SLUG[worldSlug] : worldSlug,
    subpage,
  })
}

export function getUserUrl({
  userId,
  username,
  path,
}: {
  userId: number
  username: string | null
  path?: 'feedback' | 'sessions' | 'achievements'
}): string {
  return `${WEBSITE_URL}/u/${encodeURIComponent(username ?? userId)}${
    path == null ? '' : `/${path}`
  }`
}

export function getCertifyUrl(
  topicId: number,
  options: { manual?: boolean; worldSlug?: string }
): string {
  const { worldSlug, manual = false } = options
  return `${WEBSITE_URL}/topic/${topicId}/certify${manual ? '/manual' : ''}${
    worldSlug != null ? `?${WORLD_SLUG_QUERY_PARAM_KEY}=${worldSlug}` : ''
  }`
}

export function getSeriesUrl(
  seriesId: number,
  options?: {
    path?: 'update' | 'messages' | 'members' | 'assessments' | 'feedback' | 'give-feedback'
    referralUserId?: number
    params?: QueryParams
  }
): string {
  const base = `${WEBSITE_URL}/series/${seriesId}`

  const urlSearchParams = new URLSearchParams()

  const baseUrl = options?.path == null ? base : `${base}/${options.path}`

  if (options?.referralUserId == null) {
    return baseUrl
  }

  urlSearchParams.set(SERIES_REFERRAL_USER_QUERY_PARAM, `${options.referralUserId}`)

  if (options?.params != null) {
    Object.entries(options.params).forEach(([key, value]) => {
      urlSearchParams.set(key, `${value}`)
    })
  }

  return `${baseUrl}?${urlSearchParams.toString()}`
}

export function getSessionUrl(sessionId: number): string {
  return `${WEBSITE_URL}/session/${sessionId}`
}

export function getSessionOrSeriesUrl({
  sessionId,
  seriesId,
}: {
  sessionId: number
  seriesId: number | null
}): string {
  return seriesId != null ? getSeriesUrl(seriesId) : getSessionUrl(sessionId)
}

export function getSessionUrlAsDropIn(sessionId: number) {
  return `${getSessionUrl(sessionId)}?${DROPIN_QUERY_PARAM}`
}

export function getScheduleUrl({
  past,
  sessionId,
}: {
  past: boolean
  sessionId?: number | null
}): string {
  const base = getUrl('schedule')

  const { key, options } = SHOW_DASHBOARD_SESSIONS_URL_QUERY

  const urlParams = new URLSearchParams({
    [key]: past ? options.past : options.upcoming,
  })

  if (sessionId != null) {
    urlParams.set(SESSION, `${sessionId}`)
  }

  return `${base}?${urlParams.toString()}`
}

export function getWorldUrl(
  slug: string,
  options?: {
    path?: 'sessions' | 'certify' | 'about' | 'journey' | 'moderator' | 'leaderboard' | 'programs'
    queryParams?: QueryParams
  }
): string {
  // During ssr, the slug is empty, so we use a placeholder to avoid Next.js complaining.
  const ssrSafeSlug = slug.length === 0 ? 'placeholder' : slug
  const base = `${WEBSITE_URL}/w/${ssrSafeSlug}`

  if (options == null) {
    return base
  }

  const { path, queryParams } = options

  return `${base}/${path ?? ''}${queryParamsToString(queryParams)}`
}

export function getBlogUrl(slug?: string): string {
  return `${WEBSITE_URL}/blog${slug == null ? '' : `/${slug}`}`
}

export function getGmailVerificationSearchUrl(email: string): string {
  return `https://mail.google.com/mail/u/${email}/#search/from%3A%40schoolhouse.world+in%3Aanywhere+newer_than%3A1d`
}

export function getRoadmapUrl(): string {
  return `${WEBSITE_URL}/roadmap`
}

export function getGameUrl({ path }: { path: 'play-sat' }): string {
  return `${WEBSITE_URL}/games/${path}`
}

export function getBlogCategoryUrl(category: BlogArticleCategory): string {
  return `${getBlogUrl()}/c/${category}`
}

// NOTE: A/B test for session simulator link in the training
// TODO: Remove by end of 2025, or earlier
// Experiment being run by Andrew Chang
function applyExperimentArmToTrainingUrl({
  trainingLink,
  trainingSlug,
  userId,
}: {
  trainingLink: string
  trainingSlug: string | null
  userId: number
}): string {
  const experimentArm = getExperimentArm(userId, 'training')

  if (experimentArm === 'experiment') {
    if (trainingSlug === LAY_THE_FOUNDATION_TRAINING_SLUG) {
      return 'https://form.typeform.com/to/QUBjZiKN'
    }
    if (trainingSlug === ENGAGE_ACTIVELY_TRAINING_SLUG) {
      return 'https://form.typeform.com/to/QXAyymrS'
    }
    if (trainingSlug === ADJUST_AS_NEEDED_TRAINING_SLUG) {
      return 'https://form.typeform.com/to/mGdCpYUC'
    }
    if (trainingSlug === DEVELOP_CONNECTIONS_TRAINING_SLUG) {
      return 'https://form.typeform.com/to/FOJyzUej'
    }
  }

  return trainingLink
}

export function getPersonalizedTrainingUrl({
  userId,
  trainingLink,
  trainingSlug,
  uuidTraining,
  worldSlug,
}: {
  userId: number
  trainingLink: string
  trainingSlug: string | null
  uuidTraining: string
  worldSlug?: string | null
}): string {
  const link = applyExperimentArmToTrainingUrl({
    trainingLink,
    trainingSlug,
    userId,
  })

  return `${link}#uid=${uuidTraining}${worldSlug == null ? '' : `&world=${worldSlug}`}`
}

export function getPersonalizedSurveyUrl({
  surveyUrl,
  uuidTraining,
  worldSlug,
}: {
  surveyUrl: string
  uuidTraining: string
  worldSlug?: string
}): string {
  return `${surveyUrl}#uid=${uuidTraining}${worldSlug == null ? '' : `&world=${worldSlug}`}`
}

export function getReportUrl(): string {
  return `${WEBSITE_URL}/report`
}

export function getSmsSeriesUrl(id: number): string {
  return `${WEBSITE_URL}/r/ts/${id}`
}

export function getCommunityUrl(): string {
  return `${WEBSITE_URL}/community`
}

export function getSessionSimulatorUrl(): string {
  return `${WEBSITE_URL}/session-simulator`
}

export function getValidUrl(url: string): URL | null {
  try {
    const validUrl = new URL(url, WEBSITE_URL)
    return validUrl
  } catch (err) {
    logger.error('getValidUrl: Failed to parse url', { url, err })
    return null
  }
}

export type AuthMode = 'login' | 'signup' | 'verify'

export function getAuthUrl({
  returnTo,
  user,
  mode,
}: {
  returnTo: string | null
  user: UserContextType | null
  mode: AuthMode
}): string {
  const loggedOutUrl = new URL(`${WEBSITE_URL}/api/auth/login`)

  if (mode === 'signup') {
    loggedOutUrl.searchParams.set('mode', 'signup')
  } else if (mode === 'verify') {
    loggedOutUrl.searchParams.set('mode', 'verify')
  }

  if (returnTo == null) {
    return loggedOutUrl.toString()
  }

  const validReturnTo = getValidUrl(returnTo)
  if (validReturnTo == null) {
    return loggedOutUrl.toString()
  }
  const validReturnToString = validReturnTo.toString()

  const trimmedReturnTo = validReturnToString.startsWith(WEBSITE_URL)
    ? validReturnToString.slice(WEBSITE_URL.length)
    : validReturnToString

  // If user is logged out, add returnTo parameter, otherwise just return the returnTo URL directly
  if (user == null || user.isLoggedOut || mode === 'verify') {
    loggedOutUrl.searchParams.set('returnTo', trimmedReturnTo)
    return loggedOutUrl.toString()
  }

  return validReturnToString
}

/**
 * @deprecated: Please use getAuthUrl instead
 */
export function getSignUpUrl(
  returnTo: string | null = null,
  user: UserContextType | null = null
): string {
  return getAuthUrl({ returnTo, user, mode: 'signup' })
}

/**
 * @deprecated: Please use getAuthUrl instead
 */
export function getLoginUrl(
  returnTo: string | null = null,
  user: UserContextType | null = null
): string {
  return getAuthUrl({ returnTo, user, mode: 'login' })
}

export function getLogoutUrl(): string {
  return `${WEBSITE_URL}/api/auth/logout`
}

export function getVerifyUrl(returnTo?: string): string {
  return `${WEBSITE_URL}/api/auth/login?mode=verify${
    returnTo != null ? `&returnTo=${returnTo}` : ''
  }`
}

export function getLeaderboardUrl(path?: 'key-club'): string {
  return `${WEBSITE_URL}/leaderboard/${path ?? ''}`
}

export function getReplayUrl(): string {
  return `${WEBSITE_URL}/replay`
}

export const CANNY_BUGS_URL = 'https://schoolhouse.canny.io/bugs'

export function getCannyBugReportUrl(): string {
  return CANNY_BUGS_URL
}

export function getPortfolioUrl(): string {
  return `${WEBSITE_URL}/portfolio`
}

export function getTutorAuditUrl(path?: 'leaderboard' | 'past'): string {
  return `${WEBSITE_URL}/tutor/peer-review/${path ?? ''}`
}

export function getTutorAuditPastUrl(auditId: number): string {
  return `${getTutorAuditUrl('past')}?review=${auditId}`
}

export function getTutorFeedbackUrl(
  params: { sessionId?: number; sessionFeedbackId?: number; seriesId?: number } = {}
): string {
  if (Object.keys(params).length === 0) {
    return `${WEBSITE_URL}/tutor/feedback`
  }

  const { sessionId, sessionFeedbackId, seriesId } = params

  const urlParams = new URLSearchParams()
  if (sessionId != null) {
    urlParams.set(SESSION, `${sessionId}`)
  }
  if (sessionFeedbackId != null) {
    urlParams.set(SESSION_FEEDBACK, `${sessionFeedbackId}`)
  }
  if (seriesId != null) {
    urlParams.set(SERIES, `${seriesId}`)
  }

  return `${WEBSITE_URL}/tutor/feedback?${urlParams.toString()}`
}

export function getSettingsUrl(path?: 'avatar' | 'notifications'): string {
  return `${WEBSITE_URL}/settings/${path ?? ''}`
}

export function getStoriesUrl(): string {
  return `${WEBSITE_URL}/stories`
}

export function getExploreUrl(subpage?: 'tutors'): string {
  return `${WEBSITE_URL}/explore${subpage == null ? '' : `/${subpage}`}`
}

export function getExploreAllUrl(filter?: 'series' | 'session'): string {
  return `${WEBSITE_URL}/explore/all${filter != null ? `?filter=${filter}` : ''}`
}

export function getAboutUrl(
  path?:
    | 'peer-tutoring'
    | 'terms'
    | 'terms-of-service'
    | 'safety'
    | 'privacy'
    | 'content-policy'
    | 'trust-safety'
    | 'terms-definitions'
    | 'third-parties'
    | 'cookies'
): string {
  return `${WEBSITE_URL}/about${path == null ? '' : `/${path}`}`
}

export function getPrivacyUrl(hash?: (typeof PRIVACY_TAGS)[keyof typeof PRIVACY_TAGS]): string {
  return `${getAboutUrl('privacy')}${hash != null ? `#${hash}` : ''}`
}

export function getBecomeATutorUrl(): string {
  return `${WEBSITE_URL}/become-a-tutor`
}

export function getTermsUrl(): string {
  return `${WEBSITE_URL}/about/terms`
}

export function getUtmUrlSuffix({
  campaign,
  medium,
  prefix = '?',
}: {
  campaign: string
  medium: 'email' | 'text' | 'slack'
  prefix?: '?' | '&'
}): string {
  return `${prefix}utm_campaign=${campaign}&utm_medium=${medium}`
}

export function getAdminUrl(pageName?: string): string {
  if (pageName == null) {
    return `${WEBSITE_URL}/admin`
  }
  if (IS_DEVELOPMENT && pageName.startsWith('/')) {
    throw new Error(INVALID_URL_MESSAGE)
  }
  return `${WEBSITE_URL}/admin/${pageName}`
}

export function getSessionUrlById(sessionId: number): string {
  return `${WEBSITE_URL}/session/${sessionId}`
}

export function getSessionFeedbackUrlById(sessionId: number): string {
  return `${getSessionUrlById(sessionId)}/feedback`
}

export function getSessionReflectionUrlById(sessionId: number): string {
  return `${getSessionUrlById(sessionId)}/reflection`
}

export function getTopicUrlById(topicId: number, path?: 'certify'): string {
  const base = `${WEBSITE_URL}/topic/${topicId}`

  if (path != null) {
    return `${base}/${path}`
  }

  return base
}

export function getCourseUrlBySlug(courseSlug: string | null): string {
  if (courseSlug != null && courseSlug === 'null' && typeof globalThis.window !== 'undefined') {
    logger.error('Invalid course slug', { courseSlug, stackTrace: new Error().stack })
  }
  return courseSlug == null ? `${WEBSITE_URL}/course/all` : `${WEBSITE_URL}/course/${courseSlug}`
}

export function getReviewUrlById(certificationReviewId: number): string {
  return `${WEBSITE_URL}/certification/review/${certificationReviewId}`
}

export function getTranscriptUrlByUUID(uuid: string): string {
  return `${WEBSITE_URL}/transcript/${uuid}`
}

export function getReferralLinkFromCode(referralCode: string): string {
  return addReferralCodeIfNotPresent(WEBSITE_URL, referralCode)
}

export function getCertificationUrl(
  path?: 'about' | 'get-certified' | 'review' | 'profile'
): string {
  return `${WEBSITE_URL}/certification/${path ?? ''}`
}

export function getCertificationUrlForCourse(courseName: string): string {
  return `${getCertificationUrl('get-certified')}?course=${courseName}`
}

export function getSATGetCertifiedUrl(): string {
  return getCertificationUrlForCourse(SAT_PREP_COURSE)
}

export function getPartnerLogoURL(file: string): string {
  return `${ASSETS_URL}/partner-logos/${file}`
}

export function getPublicUrl(
  postfix = '',
  options: { forceProduction: boolean } = { forceProduction: false }
): string {
  return `${options.forceProduction ? 'https://schoolhouse.world/assets' : ASSETS_URL}/public${
    postfix.startsWith('/') || postfix.length === 0 ? postfix : `/${postfix}`
  }`
}

export function getNavbarIconUrl(): string {
  return `${ASSETS_URL}/navbar`
}

export function isSHWLink(link: string): boolean {
  return link === WEBSITE_URL || link.startsWith(`${WEBSITE_URL}/`)
}

export function getReferUrl(): string {
  return `${WEBSITE_URL}/refer`
}

export function getSATOnboardingSessionsUrl(): string {
  return `${WEBSITE_URL}/sat-bootcamp/onboarding-sessions`
}

export function getSATTutorSupportLabsSessionsUrl(): string {
  return `${WEBSITE_URL}/sat-bootcamp/tutor-support-labs`
}

export function getKeyClubUrl(): string {
  return `${WEBSITE_URL}/key-club`
}

export function getSeriesUrlById(
  seriesId: number,
  options: { page: 'assessments' | 'messages' | 'members' | null } = { page: null }
): string {
  const { page } = options
  return `${WEBSITE_URL}/series/${seriesId}${page == null ? '' : `/${page}`}`
}

export function getMessagesUrl(conversationUuid?: string | null): string {
  return `${WEBSITE_URL}/messages${conversationUuid == null ? '' : `#${conversationUuid}`}`
}

export function getAdminMessagesUrl({
  conversationUuid,
  userId,
}: {
  conversationUuid?: string | null
  userId?: number | null
}): string {
  const baseUrl = `${WEBSITE_URL}/admin/messages${userId != null ? `?${USER_ID}=${userId}` : ''}`
  return `${baseUrl}${conversationUuid == null ? '' : `#${conversationUuid}`}`
}

export function getAdminConversationUrl(id: number, messageId?: number | null): string {
  const base = getAdminUrl(`conversation/${id}`)
  return messageId != null ? `${base}?message_id=${messageId}` : base
}

export function getAdminSeriesTemplateUrl(seriesTemplateId: number): string {
  return getAdminUrl(`templates/series-template/${seriesTemplateId}`)
}

export function getBreakoutSessionJoinUrl(breakoutSessionId: number): string {
  return `${WEBSITE_URL}/api/join-session-breakout?id=${breakoutSessionId}`
}

export function getSessionJoinRedirectUrl(sessionId: number, asHost?: boolean): string {
  return `${WEBSITE_URL}/api/join-session?id=${sessionId}${asHost === true ? '&as=host' : ''}`
}

export function getPartnershipsUrl(): string {
  return `${WEBSITE_URL}/partnerships`
}

export function getDonateUrl(): string {
  return 'https://schoolhouseworld.networkforgood.com/'
}

export function getStudySpacesUrl(): string {
  return `${WEBSITE_URL}/study-spaces`
}

export function getTypeformResponsesUrl(id: string): string {
  return `https://admin.typeform.com/form/${id}/results#responses`
}

export function getLiveHelpUrl({
  status = null,
}: {
  status?: 'registering' | 'hosting' | null
}): string {
  return `${WEBSITE_URL}/live-help${status !== null ? `?status=${status}` : ''}`
}

export function getLiveHelpForEducatorsUrl(): string {
  return `${WEBSITE_URL}/teachers`
}

export function getAchievementIconUrl(file: string): string {
  return `${getPublicUrl()}/progression/${file}`
}

export function getOnboardingUrl(returnTo?: string): string {
  const onboardingUrl = `${WEBSITE_URL}/onboarding`
  if (returnTo == null) {
    return onboardingUrl
  }
  const trimmedReturnTo = returnTo.startsWith(WEBSITE_URL)
    ? returnTo.slice(WEBSITE_URL.length)
    : returnTo

  return `${onboardingUrl}?returnTo=${trimmedReturnTo}`
}

export function getGiveLearnerFeedbackUrl({
  sessionId,
  seriesId,
}: {
  sessionId: number
  seriesId?: number | null
}): string {
  return seriesId == null
    ? getSessionUrlById(sessionId)
    : `${getSeriesUrl(seriesId, { path: 'give-feedback' })}`
}

export function getMeUrl(): string {
  return `${WEBSITE_URL}/me`
}

export function getTutorTreeUrl(userId: number, username: string | null): string {
  return `${getUserUrl({ userId, username })}/tutor-tree`
}

export function getCommunitySlackProfileURL(slackId: string): string {
  return `${COMMUNITY_SLACK_URL}/team/${slackId}`
}

export function getSummerCampUrl(): string {
  return `${WEBSITE_URL}/summer-camp`
}

export function getSummerCampExploreUrl(tag?: string): string {
  return `${WEBSITE_URL}/summer-camp/explore${tag != null ? `?tag=${tag}` : ''}`
}

export function getAccountSuspensionReviewUrl(id: number): string {
  return `${WEBSITE_URL}/reports/${id}/review`
}

export function getAdminSessionUrlById(sessionId: number): string {
  return getAdminUrl(`session/${sessionId}`)
}

export function getProgramEmailsAdminUrl(slug?: string): string {
  if (slug == null) {
    return getAdminUrl('program/emails')
  }
  return getAdminUrl(`program/${slug}/emails`)
}

export function getTutorAdminUrl(userId: number): string {
  return getAdminUrl(`tutor/${userId}`)
}

export function getContentFlagAdminUrl(contentFlagId: number): string {
  return getAdminUrl(`content-flag/${contentFlagId}`)
}

export function getClassroomsUrl(): string {
  return `${WEBSITE_URL}/classrooms`
}

export function getClassroomUrlById(id: number, page: ClassroomsPage): string {
  return `${WEBSITE_URL}/classrooms/${id}/${page}`
}

export function getClassroomMemberUrlById(
  classroomId: number,
  studentId: number,
  previousPage?: ClassroomsPage
): string {
  return `${getClassroomUrlById(classroomId, 'students')}/${studentId}${
    previousPage != null ? `?previous=${previousPage}` : ''
  }`
}

export function getSATBootcampUrl(
  type?: SATUserType | null,
  options?: {
    path?: 'request-consent' | 'consent' | 'request-contact'
    as?: 'parent' | null
    urlParams?: SATBootcampURLParams | null
    ref?: string
    satRef?: string
  }
): string {
  const baseUrl = `${WEBSITE_URL}/sat-bootcamp${type === 'tutor' ? '/tutor' : ''}${
    options?.path != null ? `/${options.path}` : ''
  }`

  const { as, urlParams, ref, satRef } = options ?? {}
  const queryParams = new URLSearchParams()

  if (urlParams?.type === 'invite-code') {
    queryParams.set(SAT_BOOTCAMP_INVITE_CODE_QUERY_PARAM, urlParams.inviteCode.toLowerCase())
  }

  if (urlParams?.type === 'encoded-params') {
    const { type: _, ...restParams } = urlParams

    const encodedParamsValue = encodeInvitationParams(restParams)
    queryParams.set(SAT_BOOTCAMP_ENCODED_PARAMS_QUERY_KEY, encodedParamsValue)
  }

  if (as != null) {
    queryParams.set(SAT_BOOTCAMP_AS_QUERY_PARM, as)
  }

  if (ref != null) {
    queryParams.set('ref', ref)
  }

  if (satRef != null) {
    queryParams.set(SAT_BOOTCAMP_REFERRAL_CODE_QUERY_PARAM, satRef)
  }

  const queryParamsString = queryParams.toString()

  return `${baseUrl}${queryParamsString.length > 0 ? `?${queryParamsString}` : ''}`
}

export function getSATBootcampTutorUrl(
  role: 'parent' | 'student' = 'student',
  urlParams: SATBootcampURLParams | null = null
): string {
  return getSATBootcampUrl('tutor', {
    as: role === 'parent' ? 'parent' : null,
    urlParams,
  })
}

export function getParentConsentUrl(inviteCode: string, urlParams?: URLSearchParams): string {
  if (urlParams != null) {
    urlParams.set(SAT_BOOTCAMP_INVITE_CODE_QUERY_PARAM, inviteCode)
  }
  return `${WEBSITE_URL}/parent-consent${urlParams ? `?${urlParams}` : ''}`
}

export function getSATBootcampParentConsentUrl(
  type: 'learner',
  urlParams?: SATBootcampURLParams | null
): string {
  return getSATBootcampUrl(type, { path: 'consent', urlParams })
}

export function getRequestParentConsentUrl(urlParams?: URLSearchParams | null): string {
  return `${WEBSITE_URL}/request-parent-consent${urlParams ? `?${urlParams.toString()}` : ''}`
}

export function getAssessmentUrl(assessmentId?: number): string {
  return `${WEBSITE_URL}/assessment${assessmentId == null ? '' : `/${assessmentId}`}`
}

export function getAssessmentSessionUrl(assessmentId: number, sessionId: number): string {
  return `${WEBSITE_URL}/assessment/${assessmentId}?session=${sessionId}`
}

export function getAssessmentSessionOrSeriesUrl(
  assessmentId: number,
  assignment: AssessmentAssignment
): string {
  return `${WEBSITE_URL}/assessment/${assessmentId}?${assignment.type}=${
    assignment.type === 'session' ? assignment.sessionId : assignment.seriesId
  }`
}

export function getBuildAssessmentUrl(): string {
  return `${getAssessmentUrl()}/build`
}

export function getEditAssessmentUrl(assessmentId: number): string {
  return `${getAssessmentUrl(assessmentId)}/edit`
}

export function getDuplicateAssessmentUrl(assessmentId: number): string {
  return `${getBuildAssessmentUrl()}?${DUPLICATE_OF}=${assessmentId}`
}

export function getTakeAssessmentUrl(assessmentId: number, seriesId?: number | null): string {
  const url = new URL(`${getAssessmentUrl(assessmentId)}/take`)
  if (seriesId != null) {
    url.searchParams.set(SERIES, `${seriesId}`)
  }
  return url.toString()
}

export function getViewAssessmentResultsUrl(assessmentId: number): string {
  return `${getAssessmentUrl(assessmentId)}/results`
}

export function getKaLinkageUrl(page?: 'quiz' | 'explore' | 'one-on-one'): string {
  return `${WEBSITE_URL}/ka${page != null ? `/${page}` : ''}`
}

export function getKaLinkageConfirmationUrl(
  copyVersion: 'other-subject' | 'help-channels' | 'bootcamps' | '1-on-1'
): string {
  return `${getKaLinkageUrl()}/confirmation?copy=${copyVersion}`
}

export function getCollegeAdmissionsWorkshopsUrl(type?: 'tutor'): string {
  return `${WEBSITE_URL}/college-admissions/${type ?? ''}`
}

export function getUserStoriesUrl(): string {
  return `${WEBSITE_URL}/stories`
}

export function getDeadlineHotlineUrl(): string {
  return `${WEBSITE_URL}/deadline-hotline`
}

export function getUserStoryUrl(id: number): string {
  return `${getUserStoriesUrl()}/?id=${id}`
}

export function getUnapprovedPageUrl(): string {
  return `${WEBSITE_URL}/unapproved-user`
}

export function getSearchUrl(): string {
  return `${WEBSITE_URL}/search`
}

export function getDecemberKhanAcademyBootcampsUrl(): string {
  return `${WEBSITE_URL}/algebra-bootcamp`
}

export function getVolunteerTeamCodaUrl(): string {
  return 'https://coda.io/@schoolhouse-world/volunteering-at-schoolhouse'
}

export function getBrandResourcesUrl(): string {
  return 'https://coda.io/@shw/brand-resources'
}

export function getDiscoverUrl(path?: 'programs' | 'sessions' | 'live-help'): string {
  return `${getUrl('discover')}${path != null ? `/${path}` : ''}`
}

export function getDashboardUrl(
  path?: 'programs' | 'sessions' | 'live-help' | 'community' | 'one-on-one'
): string {
  return `${getUrl('dashboard')}${path != null ? `/${path}` : ''}`
}

export function getDashboardProgramsUrl({
  programSlug,
  offeringSlug,
}: {
  programSlug?: string
  offeringSlug?: string
}): string {
  return `${getDashboardUrl('programs')}${programSlug != null ? `?program=${programSlug}` : ''}${offeringSlug != null ? `&offering=${offeringSlug}` : ''}`
}

export function getDashboardSessionsUrl({ worldSlug }: { worldSlug?: string }): string {
  return `${getDashboardUrl('sessions')}${worldSlug != null ? `?slug=${worldSlug}` : ''}`
}

export function getDashboardOneOnOneUrl({ worldSlug }: { worldSlug?: string }): string {
  return `${getDashboardUrl('one-on-one')}${worldSlug != null ? `?worlds=${worldSlug}` : ''}`
}

export function getDashboardLiveHelpUrl({
  worldSlug,
  postId,
  tab,
  filter,
}: {
  worldSlug?: string
  postId?: number
  tab?: 'get-help' | 'give-help'
  filter?: 'waiting-zoom'
}): string {
  const params = new URLSearchParams()
  if (worldSlug != null) {
    params.set('world', worldSlug)
  }
  if (postId != null) {
    params.set('p', `${postId}`)
  }
  if (filter != null) {
    params.set('filter', filter)
  }
  if (tab != null) {
    params.set('tab', tab)
  }
  const queryString = params.toString()
  return `${getDashboardUrl('live-help')}${queryString ? `?${queryString}` : ''}`
}

export function getProgramReturnToUrl({
  programSlug,
  offeringSlug,
  as = 'learner',
}: {
  programSlug: ProgramSlug
  offeringSlug?: string
  as?: 'tutor' | 'learner'
}): string {
  if (as === 'tutor') {
    return getUrl('tutor')
  }

  return getDashboardProgramsUrl({ programSlug, offeringSlug })
}

const SAT_BOOTCAMP_CURRICULUM_URL =
  'https://coda.io/@schoolhouse3/sat-bootcamp-tutor-curriculum-3-0'

export function getSATBootcampCurriculumUrl(
  page?: 'what-would-you-like-support-with-60' | 'troubleshooting-guide-60'
): string {
  return `${SAT_BOOTCAMP_CURRICULUM_URL}${page != null ? `/${page}` : ''}`
}

export function getSATBootcampMakeupGuideUrl(): string {
  return 'https://coda.io/@schoolhouse3/new-sat-bootcamp-curriculum-guide'
}

export function getSATBootcampLearnerHandbookUrl(): string {
  return 'https://coda.io/@shw/sat-bootcamp-learner-handbook'
}

export function getCoachesUrl(): string {
  return 'https://coda.io/@schoolhouse-community/shw-volunteer-teams/schoolhouse-coaches-25'
}

export function getOnboardingVideoAskUrl(): string {
  return 'https://www.videoask.com/fvvtt4nyf'
}

export function getAgeAssuranceVideoAskUrl(): string {
  return 'https://www.videoask.com/flbb3w6s8?redirect_target=self'
}

export function getYotiWebhookUrl(): string {
  return `${WEBSITE_URL}/api/webhooks/yoti`
}

export function getYotiSessionUrl({ sessionId }: { sessionId: string }): string {
  return `https://age.yoti.com?sessionId=${sessionId}&sdkId=${WEBSITE_SETTINGS.yotiSdkId}`
}

export function getAgeAssuranceCallbackUrl(): string {
  return `${WEBSITE_URL}/age-verification-complete`
}

export function getFindAMentorUrl(user: UserContextType): string {
  const urlParams = new URLSearchParams()
  urlParams.set('id', `${user.id}`)
  return `https://form.typeform.com/to/diOJYqcQ#${urlParams.toString()}`
}

export function getVolunteerUrl(): string {
  return `${WEBSITE_URL}/volunteer`
}

export function getVolunteerOpenRolesPageUrl(): string {
  return `${getVolunteerUrl()}/open-roles`
}

export function getVolunteerTeamUrl(slug: string): string {
  return `${getVolunteerUrl()}/team/${slug}`
}

export function getVolunteerRoleUrl(roleId: number): string {
  return `${getVolunteerUrl()}/role/${roleId}`
}

export function getVolunteerRoleApplicationUrl(roleId: number): string {
  return `${getVolunteerRoleUrl(roleId)}/apply`
}

export function getMyVolunteerApplicationsUrl(): string {
  return `${getVolunteerUrl()}/my-applications`
}

export function getVolunteerApplicationReviewUrl(): string {
  return `${getVolunteerUrl()}/application/review`
}

export function getReportVolunteerHoursTypeformUrl(): string {
  return 'https://form.typeform.com/to/LzDWGOtH?typeform-source=schoolhouse.world'
}

export function getDialoguesLandingPageUrl({
  utmContent = null,
}: {
  utmContent?: string | null
}): string {
  return `${WEBSITE_URL}/dialogues${utmContent != null ? `?utm_content=${encodeURIComponent(utmContent)}` : ''}`
}

export function getDialoguesDiscoveryPageUrl(): string {
  return `${WEBSITE_URL}/dialogues/events`
}

export function getDialoguesPostSessionSurveyUrl({
  seriesId,
  sessionId,
}: {
  seriesId: number
  sessionId: number
}): string {
  return getSeriesUrl(seriesId, { params: { reflectionSessionId: sessionId } })
}

export function getDialoguesVideoUrl(): string {
  return 'https://youtube.com/shorts/3JfS21OKrZ4'
}

export function getDialoguesFeedbackPageUrl(): string {
  return `${WEBSITE_URL}/learner-feedback/dialogues`
}

export function getExampleDiscussionGuideLink(): string {
  return 'https://drive.google.com/file/d/1xT-S7-imIVqtPvjz7qhwdOBz8bSO44pz/view'
}

export function getLEADOnePagerUrl(): string {
  return 'https://docs.google.com/document/d/1D0waJNwpVPBWzr30Ybx7P282LMNXvzE19rQYwf-PRlc'
}

export function getTutorFeedbackFeedbackUrl(): string {
  return 'https://form.typeform.com/to/Dwtde5Fp'
}

export function getLEADPeerReviewRubricUrl(): string {
  return 'https://coda.io/d/_d7M_FtCHkB4/Standard-Tutoring-Rubric_suj91dWy'
}

// We will be adding a community tab with channels that are not linked to subworlds (i.e. global). TODO: remove 'global' from this function name once we've removed subworld posts and channels.
export function getGlobalChannelUrl(channelId: number): string {
  return `${getDashboardUrl('community')}/c/${channelId}`
}

export function getCommunityHomeUrl(): string {
  return `${getDashboardUrl('community')}/home`
}

export function getCommunityPostUrl({
  channelId,
  postId,
}: {
  channelId: number
  postId: number
}): string {
  return `${getDashboardUrl('community')}/c/${channelId}?${QUERY_POST_ID}=${postId}`
}

export function getFingerprintUrl(): string {
  return 'https://www.fingerprint.com/'
}

export function getDialoguesClubPresidentInfoUrl(): string {
  return 'https://coda.io/d/_dXU2-F-xKRr/Club-President-Information_suv8zO7_'
}

export function getDialoguesClubPresidentRoleExpectations(): string {
  return 'https://coda.io/d/_dXU2-F-xKRr/President-Role-Expectations_suDARYLt'
}

export function get2025CommunitySurveyUrl(): string {
  return 'https://form.typeform.com/to/BLJo3JBf?utm_source=webapp_banner'
}

export function getPostUrl(
  params:
    | { type: 'live-help'; postId: number }
    | { type: 'community'; channelId: number; postId: number }
): string {
  if (params.type === 'live-help') {
    return getDashboardLiveHelpUrl({ postId: params.postId })
  }
  return getCommunityPostUrl({ channelId: params.channelId, postId: params.postId })
}

export function getPostUrlFromContext({
  postId,
  channelId,
  hasWorld,
}: {
  postId: number
  channelId: number
  hasWorld: boolean
}): string {
  // `hasWorld` is used as a proxy to determine whether the post is a live help post or a community post. A cleaner way to do this would be having an additional column on post indicating whether its live help or community.
  if (hasWorld) {
    return getPostUrl({ type: 'live-help', postId })
  }
  return getPostUrl({ type: 'community', postId, channelId })
}

export * as email from './email'
export * as helpers from './helpers'
export * as peerReview from './peerReview'
export { getQueryParamArray } from './queryParams'
export * as trustSafety from './trustSafety'
export { WEBSITE_URL, faq }
